import React from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

import type { DataExtractionPropertyFormValue } from '@/lib/data-extraction';

import { formatPropertyTypeLabel } from './data-extraction-prompt.utils';

type DataExtractionPropertiesListProps = {
  properties: DataExtractionPropertyFormValue[];
  arrayError?: string;
  addButtonLabel?: string;
  helperText?: string;
  readOnly?: boolean;
  emptyStateMessage?: string;
  onAddProperty: () => void;
  onEditProperty: (property: DataExtractionPropertyFormValue) => void;
  onRemoveProperty: (propertyId: string) => void;
};

export const DataExtractionPropertiesList = React.memo(
  ({
    properties,
    arrayError,
    addButtonLabel = 'Add field',
    helperText,
    readOnly = false,
    emptyStateMessage = 'No properties added yet. Add fields to shape the extraction response.',
    onAddProperty,
    onEditProperty,
    onRemoveProperty,
  }: DataExtractionPropertiesListProps) => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <Label>Data Extraction fields</Label>
            {helperText && <p className="text-sm text-muted-foreground">{helperText}</p>}
          </div>
          <Button variant="outline" type="button" onClick={onAddProperty}>
            <Plus className="h-4 w-4" />
            {addButtonLabel}
          </Button>
        </div>

        {properties.length > 0 ? (
          <div className="space-y-2">
            {properties.map(property => (
              <div
                key={property.id}
                className="flex flex-col gap-3 rounded-lg border px-3 py-3 transition-colors hover:bg-muted/50 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{property.identifier}</span>
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                      {formatPropertyTypeLabel(property)}
                    </span>
                    {property.required && (
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                        required
                      </span>
                    )}
                  </div>
                  {property.description && (
                    <p className="text-sm text-muted-foreground">{property.description}</p>
                  )}
                  {property.enumValues.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Enum: {property.enumValues.join(', ')}
                    </p>
                  )}
                </div>

                {!readOnly && (
                  <div className="flex items-center gap-1 self-end sm:self-start">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      type="button"
                      onClick={() => onEditProperty(property)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      type="button"
                      className="hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => onRemoveProperty(property.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed px-4 py-5 text-sm text-muted-foreground">
            {emptyStateMessage}
          </div>
        )}

        {arrayError && <p className="text-sm text-destructive">{arrayError}</p>}
      </div>
    );
  }
);
